import React, {
  useRef,
  useMemo,
  useState,
  forwardRef,
  useContext,
  useCallback,
} from 'react';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { useDDPCall } from '@zedoc/ddp-connector';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Random } from '@zedoc/random';
import { getLeafErrors } from '@zedoc/check-schema';
import { useReconcile } from '@zedoc/react-hooks';
import {
  apiZedocCreateParticipation,
  apiZedocUpdateParticipation,
} from '../../../common/api/zedoc';
import {
  VARIABLE_WIDGET_TYPE__RELATIVE_DATE,
  PARTICIPATION_STATE_MACHINE,
  PARTICIPATION_STATE__INITIAL,
} from '../../../common/constants';
import {
  PROJECT_PROFILE_CREATE_PARTICIPATION,
  PROJECT_PROFILE_UPDATE_PARTICIPATION,
  PROJECT_PROFILE_DELETE_PARTICIPATION,
  PATIENT_ACCESS_PATIENT_PII_VARIABLES,
} from '../../../common/permissions';
import Variable from '../../../common/models/Variable';
import Project from '../../../common/models/Project';
import Recipient from '../../../common/models/Recipient';
import Participation from '../../../common/models/Participation';
import FormFieldRelativeDate from './FormFieldRelativeDate';
import FormFieldStudyNo from './FormFieldStudyNo';
import Stack from '../../../common/components/primitives/Stack';
import Loading from '../../../common/components/Loading';
import { VARIABLE_ID__NATIONAL_ID } from '../../../common/constants/Variables';
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../../utils/notify';
import branding from '../../../utils/branding';
import Dialog from '../../Dialog';
import Form from '../../forms/Form';
import usePermission from '../../../utils/usePermission';
import usePermissionsRealm from '../../../utils/usePermissionsRealm';
import FormFieldState, {
  getPayload,
  getNonEditableKeys,
  coincidesWithNonEditableKeys,
} from './FormFieldState';
import FormFieldContext from './FormFieldContext';
import FormFieldProjectId from './FormFieldProjectId';

const ConnectedFormFieldState = forwardRef((props, forwardedRef) => {
  const { previousState, payload } = useContext(FormFieldContext);
  return (
    <FormFieldState
      ref={forwardedRef}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      payload={payload}
      previousState={previousState}
      stateMachine={PARTICIPATION_STATE_MACHINE}
    />
  );
});

const ProjectProfileDialog = ({
  'data-testid': testId,
  project,
  variables,
  recipient,
  participation,
  onCancel,
  onSubmitted,
  onChangeProjectId,
  visible,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const projectProfile = useRef();

  const projectId = project?._id;

  const recipientId = recipient?._id;
  const participationId = participation?._id;

  const initialParticipation = useMemo(() => {
    if (loading) {
      return null;
    }
    return participation;
  }, [loading, participation]);

  // NOTE: Can use this to show a warning if we detect that patient
  //       is already in this project.
  // const anotherParticipation = useSelector(
  //   ParticipationSelect.one()
  //     .where({
  //       projectId,
  //       recipientId,
  //     })
  //     .satisfying(p => !p.isDischarged()),
  // );

  const { ddpCall, ddpIsPending } = useDDPCall();

  const { domains: createRealm } = usePermissionsRealm(
    [PROJECT_PROFILE_CREATE_PARTICIPATION],
    {
      scope: project?.getDomains() ?? [],
    },
  );

  const { domains: deleteRealm } = usePermissionsRealm(
    [PROJECT_PROFILE_DELETE_PARTICIPATION],
    {
      scope: project?.getDomains() ?? [],
    },
  );

  const [nextParticipation, setNextParticipation] = useState(undefined);

  const evaluateNextParticipation = useCallback(
    (formValues) => {
      const rawParticipation = initialParticipation
        ? cloneDeep(initialParticipation.raw)
        : {};
      const variablesById = keyBy(variables, '_id');
      forEach(formValues.variables, (value, variableId) => {
        const variable = variablesById[variableId];
        if (variable && variable.isParticipation() && value !== undefined) {
          variable.setValue(rawParticipation, value, {
            createRealm,
            deleteRealm,
          });
        }
      });
      return new Participation(rawParticipation);
    },
    [initialParticipation, variables, createRealm, deleteRealm],
  );

  const handleOnChange = useCallback(
    (formValues) => {
      setNextParticipation(evaluateNextParticipation(formValues));
    },
    [evaluateNextParticipation],
  );

  const previousState = participationId
    ? initialParticipation?.state ?? PARTICIPATION_STATE__INITIAL
    : PARTICIPATION_STATE__INITIAL;

  const state = (nextParticipation ?? initialParticipation)?.state;
  const trackId = (nextParticipation ?? initialParticipation)?.trackId;

  const payload = useReconcile(
    useMemo(() => {
      return getPayload(
        PARTICIPATION_STATE_MACHINE,
        nextParticipation,
        participation,
      );
    }, [nextParticipation, participation]),
  );

  const nonEditableKeys = useReconcile(
    useMemo(() => {
      return getNonEditableKeys(
        PARTICIPATION_STATE_MACHINE,
        payload,
        previousState,
        state,
      );
    }, [payload, previousState, state]),
  );

  const validateConstraints = useCallback(
    (formValues) => {
      const rawParticipation = evaluateNextParticipation(formValues).raw;
      const modelErrors = getLeafErrors(
        Participation.validate(rawParticipation),
      );
      const formErrors = {
        variables: {},
      };
      forEach(variables, (variable) => {
        const variableKey = variable.getKey(rawParticipation);
        if (
          variable.isParticipation() &&
          !coincidesWithNonEditableKeys(nonEditableKeys, variableKey)
        ) {
          const error = get(modelErrors, variableKey);
          if (error) {
            formErrors.variables[variable._id] = error;
          }
        }
      });
      if (!isEmpty(formErrors.variables)) {
        projectProfile.current.setErrors(formErrors);
        return Promise.reject(
          new Error('confirmations:validateQuestionnaire.error'),
        );
      }
      return Promise.resolve(formValues);
    },
    [variables, nonEditableKeys, evaluateNextParticipation],
  );

  const [conflictingRecipientId, setConflictingRecipientId] = useState('');
  const [conflictingStudyNo, setConflictingStudyNo] = useState(false);
  const [studyNoWillUpdate, setStudyNoWillUpdate] = useState(false);

  // NOTE: Overrides accumulate across re-submits so that, e.g., confirming the
  //       identifier conflict and then the studyNo conflict in sequence does
  //       not lose the first confirmation and ping-pong the user between the
  //       two popovers.
  const proceedOverridesRef = useRef({
    proceedEvenIfIdentifierExists: false,
    proceedEvenIfStudyNoExists: false,
  });
  const resetProceedOverrides = useCallback(() => {
    proceedOverridesRef.current = {
      proceedEvenIfIdentifierExists: false,
      proceedEvenIfStudyNoExists: false,
    };
  }, []);

  const handleApiErrors = useCallback(
    (error) => {
      if (error && /\.identifierConflict$/.test(error.error)) {
        setConflictingRecipientId(error.details.recipientId);
        setConflictingStudyNo(false);
        setStudyNoWillUpdate(false);
        return;
      }
      if (error && /\.studyNoConflict$/.test(error.error)) {
        setConflictingStudyNo(true);
        setConflictingRecipientId('');
        setStudyNoWillUpdate(false);
        return;
      }
      if (error && /\.studyNoExists$/.test(error.error)) {
        setStudyNoWillUpdate(true);
        setConflictingRecipientId('');
        setConflictingStudyNo(false);
        return;
      }
      // Server-side waitForTransition timed out (30 s) but the request is in
      // the queue and will be processed in the background. Show an
      // informational toast (not a red error) and close the dialog so the
      // user doesn't retry and create duplicates.
      //
      // Explicit allow-list (vs an open-ended /Processing$/ regex) so a
      // future unrelated server code that happens to end with "Processing"
      // can't accidentally hijack the friendly-warning path. Update this
      // list when adding a new *Processing code on the server side.
      if (
        error &&
        ['enrolmentProcessing', 'participationUpdateProcessing'].includes(
          error.error,
        )
      ) {
        setConflictingRecipientId('');
        setConflictingStudyNo(false);
        setStudyNoWillUpdate(false);
        resetProceedOverrides();
        notifyWarning({
          message: error.reason || 'Request is taking longer than expected',
          description:
            (error.details && error.details.description) ||
            'Your request has been received and is still being processed. You may continue working while processing completes in the background.',
        });
        if (onCancel) {
          onCancel();
        }
        return;
      }
      setConflictingRecipientId('');
      setConflictingStudyNo(false);
      setStudyNoWillUpdate(false);
      resetProceedOverrides();
      notifyError()(error);
    },
    [
      setConflictingRecipientId,
      setConflictingStudyNo,
      setStudyNoWillUpdate,
      resetProceedOverrides,
      onCancel,
    ],
  );

  const handleOnOk = useCallback(
    (_, overrides = {}) => {
      proceedOverridesRef.current = {
        ...proceedOverridesRef.current,
        ...overrides,
      };
      if (ddpIsPending) {
        return;
      }
      const { proceedEvenIfIdentifierExists, proceedEvenIfStudyNoExists } =
        proceedOverridesRef.current;

      if (participationId) {
        projectProfile.current
          .submit()
          .then(validateConstraints)
          .then((formValues) => {
            // Before refactor NHI hide
            if (
              formValues.variables &&
              formValues.variables[VARIABLE_ID__NATIONAL_ID] &&
              formValues.variables[VARIABLE_ID__NATIONAL_ID] === '********'
            ) {
              // If the national ID field is not changed, we should not send it to the server
              delete formValues.variables[VARIABLE_ID__NATIONAL_ID];
            }
            return ddpCall(
              apiZedocUpdateParticipation.withParams({
                correlationId: Random.id(),
                participationId,
                ...formValues,
              }),
            )
              .then(({ details }) => {
                if (onSubmitted) {
                  return onSubmitted(details);
                }
                return undefined;
              })
              .then(
                notifySuccess(
                  t('confirmations:editRecipient.success', {
                    context: branding,
                  }),
                ),
              );
          })
          .then(onCancel)
          .catch(handleApiErrors);
      } else {
        projectProfile.current
          .submit()
          .then(validateConstraints)
          .then((formValues) => {
            return ddpCall(
              apiZedocCreateParticipation.withParams(
                omitBy(
                  {
                    correlationId: Random.id(),
                    projectId,
                    recipientId,
                    proceedEvenIfIdentifierExists: recipientId
                      ? true
                      : proceedEvenIfIdentifierExists,
                    proceedEvenIfStudyNoExists,
                    ...formValues,
                  },
                  isNil,
                ),
              ),
            )
              .then(({ details }) => {
                if (onSubmitted) {
                  return onSubmitted(details);
                }
                setConflictingRecipientId('');
                setConflictingStudyNo(false);
                setStudyNoWillUpdate(false);
                resetProceedOverrides();
                return undefined;
              })
              .then(
                notifySuccess(
                  t('confirmations:addToProject.success', {
                    name: project?.name,
                    context: branding,
                  }),
                ),
              );
          })
          .then(onCancel)
          .catch(handleApiErrors);
      }
    },
    [
      projectProfile,
      projectId,
      project?.name,
      recipientId,
      participationId,
      onCancel,
      onSubmitted,
      t,
      ddpCall,
      ddpIsPending,
      handleApiErrors,
      validateConstraints,
      resetProceedOverrides,
    ],
  );

  const canUpdateParticipation = usePermission(
    PROJECT_PROFILE_UPDATE_PARTICIPATION,
    {
      relativeTo: participation?.getDomains(),
    },
  );

  const canSeePII = usePermission([PATIENT_ACCESS_PATIENT_PII_VARIABLES], {
    relativeTo: recipient?.getDomains(),
  });

  const schema = useMemo(() => {
    const newSchema = {
      type: 'object',
      properties: {
        variables: {
          type: 'object',
          required: [],
          properties: {},
          dependencies: {},
        },
      },
    };
    forEach(variables, (variable) => {
      if (variable) {
        if (variable.isPII() && !canSeePII) {
          newSchema.properties.variables.properties[variable._id] = false;
        } else {
          newSchema.properties.variables.properties[variable._id] =
            variable.getJsonSchema({
              projectId,
              language: i18n.language,
              allowedDomains: createRealm,
            });
          if (variable.compulsory) {
            newSchema.properties.variables.required.push(variable._id);
          }
        }
        // switch (variable._id) {
        //   case VARIABLE_ID__PATIENT_BASELINE: {
        //     newSchema.properties.variables.properties[`${variable._id}:overwrite`] = {
        //       type: 'boolean',
        //     };
        //     break;
        //   }
        //   default: {
        //     // ...
        //   }
        // }
      }
    });
    return newSchema;
  }, [canSeePII, createRealm, variables, i18n.language, projectId]);

  const initialValues = useMemo(() => {
    if (loading) {
      return null;
    }
    const allVariables = {
      variables: {},
    };
    const context = {
      recipient,
      participation,
    };
    forEach(variables, (variable) => {
      const value = variable.getFromContext(context);
      if (!isNil(value)) {
        allVariables.variables[variable._id] = value;
      }
    });
    return allVariables;
  }, [loading, variables, recipient, participation]);

  const isNewParticipation = !participationId;

  const fields = useMemo(() => {
    const newFields = {
      '': {
        children: ['variables'],
      },
      variables: {
        label: '',
        children: [],
      },
    };
    forEach(variables, (variable) => {
      const field = {
        testLabel: variable.name,
        disabled:
          !variable.isEditable(isNewParticipation) ||
          coincidesWithNonEditableKeys(nonEditableKeys, variable.getKey()),
      };
      newFields[`variables.${variable._id}`] = field;
      newFields.variables.children.push(variable._id);
      if (variable.isPII() && !canSeePII) {
        // NOTE: The reason this is needed is because schema for PII fields
        //       will be "false" and so it will not have any "title" assigned to it.
        const variableSchema = variable.getJsonSchema({
          projectId,
          language: i18n.language,
        });
        if (variableSchema) {
          field.label = variableSchema.title;
        }
      }
      if (variable.isIdentifier()) {
        field.disabled = field.disabled || !!conflictingRecipientId;
        if (
          project?.onlyOneParticipationPerPatient ||
          project?.isClinicalEventProject
        ) {
          field.popover = {
            title: t(
              'confirmations:existIdentifierForSingleParticipation.title',
            ),
            type: 'danger',
            size: 'large',
            placement: 'right-start',
            content: (
              <p className="text-sm">
                {t(
                  'confirmations:existIdentifierForSingleParticipation.description',
                )}
              </p>
            ),
            visible: !!conflictingRecipientId,
            onOk: () => setConflictingRecipientId(''),
          };
        } else {
          field.popover = {
            title: t('confirmations:overwriteIdentifier.title'),
            type: 'danger',
            size: 'large',
            placement: 'right-start',
            content: (
              <p className="text-sm">
                {t('confirmations:overwriteIdentifier.description')}
              </p>
            ),
            visible: !!conflictingRecipientId,
            onCancel: () => setConflictingRecipientId(''),
            // NOTE: Have to pass empty first argument
            onOk: () =>
              handleOnOk(null, { proceedEvenIfIdentifierExists: true }),
          };
        }
        // FIXME: This is a temporary solution to mask the national health identifier
        // TECH-2473 requires masking the national health identifier
        // - check if user is trying to edit the national health identifier
        // - if so, hide the field
        if (recipient && variable._id === VARIABLE_ID__NATIONAL_ID) {
          field.component = () => <></>;
        }
      }
      if (variable.isParticipationStudyNo()) {
        // studyNo
        field.component = FormFieldStudyNo;
        field.disabled =
          field.disabled || !!conflictingStudyNo || !!studyNoWillUpdate;
        if (studyNoWillUpdate) {
          field.popover = {
            title: t('confirmations:studyNoExists.title'),
            type: 'danger',
            size: 'large',
            placement: 'right-start',
            content: (
              <p className="text-sm">
                {t('confirmations:studyNoExists.description')}
              </p>
            ),
            visible: !!studyNoWillUpdate,
            onCancel: () => setStudyNoWillUpdate(false),
            // NOTE: Have to pass empty first argument. The override is
            //       accumulated in a ref, so any previously granted overrides
            //       (e.g. identifier) are preserved on this re-submit.
            onOk: () => handleOnOk(null, { proceedEvenIfStudyNoExists: true }),
          };
        } else {
          field.popover = {
            title: t('confirmations:studyNoConflict.title'),
            type: 'danger',
            size: 'large',
            placement: 'right-start',
            content: (
              <p className="text-sm">
                {t('confirmations:studyNoConflict.description')}
              </p>
            ),
            visible: !!conflictingStudyNo,
            onOk: () => setConflictingStudyNo(false),
          };
        }
      } else if (variable.isParticipationState()) {
        // state
        field.component = ConnectedFormFieldState;
      }
      switch (variable.widgetType) {
        case VARIABLE_WIDGET_TYPE__RELATIVE_DATE: {
          field.component = FormFieldRelativeDate;
          break;
        }
        default: {
          // ...
        }
      }
    });
    return newFields;
  }, [
    t,
    canSeePII,
    i18n.language,
    variables,
    isNewParticipation,
    projectId,
    nonEditableKeys,
    conflictingRecipientId,
    conflictingStudyNo,
    studyNoWillUpdate,
    handleOnOk,
  ]);

  const fieldContext = useReconcile({
    projectId,
    trackId,
    state,
    previousState,
    payload,
  });

  return (
    <Dialog
      data-testid={testId}
      size="xl"
      title={
        !isNewParticipation || loading
          ? t('editRecipient', {
              context: branding,
            })
          : `${t('addToProject', {
              context: branding,
            })} ${project?.name ?? ''}`
      }
      onOk={handleOnOk}
      okText={
        !isNewParticipation || loading ? t('save') : t('saveAndAddToProject')
      }
      isOkDisabled={
        (!isNewParticipation && !canUpdateParticipation) ||
        !!conflictingRecipientId ||
        !!conflictingStudyNo ||
        !!studyNoWillUpdate
      }
      isCancelDisabled={
        !!conflictingRecipientId || !!conflictingStudyNo || !!studyNoWillUpdate
      }
      onCancel={onCancel}
      visible={visible}
      loading={loading || ddpIsPending}
      isScrollable={
        !conflictingRecipientId && !conflictingStudyNo && !studyNoWillUpdate
      }
    >
      <Stack space={4}>
        {onChangeProjectId && (
          <FormFieldProjectId
            value={projectId ?? ''}
            onChange={onChangeProjectId}
          />
        )}
        {!loading && projectId && (
          <FormFieldContext.Provider value={fieldContext}>
            <Form
              data-testid="project-profile-form"
              key={projectId}
              ref={projectProfile}
              name={
                recipientId
                  ? `project_profile_${projectId}_${recipientId}`
                  : `project_profile_${projectId}`
              }
              initialValues={initialValues}
              onChange={handleOnChange}
              schema={schema}
              fields={fields}
            />
          </FormFieldContext.Provider>
        )}
        {loading && <Loading />}
      </Stack>
    </Dialog>
  );
};

ProjectProfileDialog.propTypes = {
  'data-testid': PropTypes.string,
  project: PropTypes.instanceOf(Project),
  variables: PropTypes.arrayOf(PropTypes.instanceOf(Variable)),
  recipient: PropTypes.instanceOf(Recipient),
  participation: PropTypes.instanceOf(Participation),
  onCancel: PropTypes.func,
  onSubmitted: PropTypes.func,
  onChangeProjectId: PropTypes.func,
  visible: PropTypes.bool,
  loading: PropTypes.bool,
};

ProjectProfileDialog.defaultProps = {
  'data-testid': 'project-profile-dialog',
  project: null,
  variables: [],
  recipient: null,
  participation: null,
  onCancel: null,
  onSubmitted: null,
  onChangeProjectId: null,
  visible: true,
  loading: true,
};

export default ProjectProfileDialog;
